import { isValidEmail, isValidPhone, getTerminalDateWithOffset } from "../../../utils/utils";
import {
  convertDateTimeToTimeString,
  getDateWithUtcHoursAndMinFromTimeString,
} from "../../../../../dataGrid/gridUtilities";
import {getDefaultPaymentInfo} from "./Payment/util"
import appConstants from "../../../../../../appConstants";
import { MIN_VALUE } from "../common/constants";

const getDefaultDeliveryStopInfo = (stopData, getDefaultExceptionDetail) => {
  return stopData.shipments.map((shipment) => {
    return {
      proId: shipment.proId,
      editInfo: {
        deliveryStatus: "",
        exceptionType: "",
        accessorials: [],
        limitedAccessCode: "",
        returnCode: "",
        customerName: null,
        isShrinkWrap: false,
        isExcessiveLength: appConstants.EXCESSIVE_SHIPMENT.NO,
        palletCount: "",
        ...getDefaultPaymentInfo(),
        ...getDefaultExceptionDetail(),
      },
      errorInfo: {},
    };
  });
};

const getDefaultStopTimeInfo = (
  isDriverArrived,
  actualArrivalTime,
  departureTime
) => {
  return {
    stopTime: {
      arrivalTime:
        isDriverArrived && actualArrivalTime
          ? getDateWithUtcHoursAndMinFromTimeString(actualArrivalTime)
          : null,
      departureTime: departureTime ?? null,
    },
    stopTimeError: {
      arrivalTime: null,
      departureTime: null,
    },
  };
};

const getDepartureTimeError = (value, { arrivalTime }) => {
  if (!value) {
    return "* Required";
  }
  if (new Date(value) < new Date(arrivalTime)) {
    return `Departure Time should be greater than ${convertDateTimeToTimeString(
      arrivalTime
    )}`;
  }
  return null;
};

const getStopTimeFieldError = (field, value, stopTimeInfo) => {
  switch (field) {
    case "arrivalTime":
      return value ? null : "* Required";
    case "departureTime":
      return getDepartureTimeError(value, stopTimeInfo);
    default:
      return null;
  }
};

const getStopTimeErrorInfo = (stopTime) => {
  let errorInfo = {};
  Object.keys(stopTime).forEach((fieldName) => {
    errorInfo[fieldName] = getStopTimeFieldError(
      fieldName,
      stopTime[fieldName],
      stopTime
    );
  });
  return { stopTime, stopTimeError: errorInfo };
};

const isStopTimeDetailsInValid = (stopTimeDetails) => {
  const error = Object.values(stopTimeDetails.stopTimeError).filter(
    (value) => value
  ).length;
  return Boolean(error);
};

const getDefaultReceiverDetailsInfo = (defaultSCACCode="") => {
  return {
    receiver: {
      receiverId: null,
      receiverName: null,
      receiverEmail: null,
      receiverCellPhone: null,
      scacCode: defaultSCACCode ?? "",
    },
    receiverError: {
      receiverName: null,
      receiverEmail: null,
      receiverCellPhone: null,
      scacCode: null,
    },
  };
};

const getReceiverEmailError = (value) => {
  if (value && !isValidEmail(value)) {
    return "Invalid email";
  }
  return null;
};


const getReceiverNumberError = (value) => {
  if (value && !isValidPhone(value)) {
    return "Invalid phone number";
  }
  return null;
};

const getReceiverDetailsFieldError = (field, value, validateScacCode) => {
  switch (field) {
    case "receiverName":
      return value ? null : "* Required";
    case "receiverEmail":
      return getReceiverEmailError(value);
    case "receiverCellPhone":
      return getReceiverNumberError(value);
    case "scacCode":
      return validateScacCode && !value ? "* Required" : null;
    default:
      return null;
  }
};

const getReceiverDetailsErrorInfo = (receiver, validateScacCode) => {
  let errorInfo = {};
  Object.keys(receiver).forEach((fieldName) => {
    errorInfo[fieldName] = getReceiverDetailsFieldError(
      fieldName,
      receiver[fieldName],
      validateScacCode
    );
  });
  return { receiver, receiverError: errorInfo };
};

const isReceiverDetailsInValid = (receiverDetails) => {
  const error = Object.values(receiverDetails.receiverError).filter(
    (value) => value
  ).length;
  return Boolean(error);
};

const getProLabelAndValue = (shipments) => {
  return {
    proDetailsLabel: "Pro Number(s)",
    proDetailsValue: shipments.map((pro) => pro.proId)?.join("; ") || "",
  };
};

const getShipmentFreightBill = (freightBillData, proId) => {
  return freightBillData.find(
    (proFreightBill) => proFreightBill.proId === proId
  )?.freightBillDetails;
};

const getExcessiveLengthError = (value, trailerLength) => {
  if (!value || value < MIN_VALUE.DELIVERY_PRO_FORM.EXCESSIVE_LENGTH) {
    return "Enter a value more than or equal to 8 feet";
  } else if (value > trailerLength) {
    return `Enter a value less than or equal to ${trailerLength} feet`;
  }
  return null;
};

const getStopTimeWithOffset = ({ arrivalTime, departureTime }, offset) => {
  return {
    arrivalTime: arrivalTime
      ? getTerminalDateWithOffset(arrivalTime, offset)
      : arrivalTime,
    departureTime: departureTime
      ? getTerminalDateWithOffset(departureTime, offset)
      : departureTime
  };
};

export {
  getDefaultDeliveryStopInfo,
  getDefaultStopTimeInfo,
  getDefaultReceiverDetailsInfo,
  getReceiverDetailsFieldError,
  getReceiverDetailsErrorInfo,
  isReceiverDetailsInValid,
  getStopTimeErrorInfo,
  isStopTimeDetailsInValid,
  getStopTimeFieldError,
  getProLabelAndValue,
  getDepartureTimeError,
  getShipmentFreightBill,
  getExcessiveLengthError,
  getStopTimeWithOffset,
};
